import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { apiClient, ApiError } from '@/services/apiClient';
import type { Employee } from '@/types/timesheet';
import { LoadingPlaceholder, ErrorPlaceholder } from './LoadingPlaceholder';
import { updateOrganisationId } from '@/lib/auth-fetch';
import { onAccessRevoked } from '@/services/accessRevoked';

interface BootstrapGateProps {
  children: React.ReactNode;
}

type BootstrapState = 'loading' | 'ready' | 'error' | 'no-access';

const EVO_ORGANISATION_ID_KEY = 'evo_organisation_id';
const PLATFORM_USER_ID_KEY = 'platform_user_id';

interface EmployeeWithTelemetryData extends Employee {
  evoOrganisationId?: string;
  platformUserId?: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export function BootstrapGate({ children }: BootstrapGateProps) {
  const { isProduction } = useAuth();
  const { setCurrentEmployeeFromAuth } = useTimesheet();
  
  const [state, setState] = useState<BootstrapState>('loading');
  const retryCountRef = useRef(0);
  const isBootstrappingRef = useRef(false);

  useEffect(() => {
    if (!isProduction) {
      setState('ready');
      return;
    }

    if (isBootstrappingRef.current) {
      return;
    }

    const bootstrap = async (): Promise<void> => {
      try {
        const employee = await apiClient.get<EmployeeWithTelemetryData>('/employees/me');
        
        if (employee && employee.id) {
          setCurrentEmployeeFromAuth(employee);
          
          // Store telemetry data in sessionStorage for Application Insights
          // and sync the in-memory authConfig so subsequent API calls use the
          // correct X-Organisation-Id header (critical after an org switch).
          if (employee.evoOrganisationId) {
            sessionStorage.setItem(EVO_ORGANISATION_ID_KEY, employee.evoOrganisationId);
            updateOrganisationId(employee.evoOrganisationId);
          }
          // Log platformUserId status for debugging
          if (employee.platformUserId) {
            sessionStorage.setItem(PLATFORM_USER_ID_KEY, employee.platformUserId);
          } else {
          }
          
          setState('ready');
        } else {
          throw new Error('Invalid employee data received');
        }
      } catch (error) {
        console.error('[BootstrapGate] Failed to fetch employee:', error);

        if (error instanceof ApiError && error.status === 403) {
          setState('no-access');
          return;
        }
        
        retryCountRef.current += 1;
        
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = RETRY_DELAY_MS * Math.pow(2, retryCountRef.current - 1);
          setTimeout(() => {
            bootstrap();
          }, delay);
        } else {
          console.error(`[BootstrapGate] Max retries (${MAX_RETRIES}) reached, showing error state`);
          setState('error');
        }
      }
    };

    isBootstrappingRef.current = true;
    retryCountRef.current = 0;
    setState('loading');
    bootstrap();
  }, [isProduction, setCurrentEmployeeFromAuth]);

  // Listen for access-revoked events dispatched by apiClient on any 403 AccessRevoked response
  useEffect(() => {
    if (!isProduction) return;
    return onAccessRevoked(() => setState('no-access'));
  }, [isProduction]);

  if (!isProduction) {
    return <>{children}</>;
  }

  if (state === 'loading') {
    return (
      <LoadingPlaceholder
        message="Setting up your workspace"
        description="Authenticating and loading your data..."
      />
    );
  }

  if (state === 'no-access') {
    return (
      <ErrorPlaceholder
        title="Access Denied"
        message="Your access to this application has been removed. Please contact your administrator."
      />
    );
  }

  if (state === 'error') {
    return (
      <ErrorPlaceholder
        title="An Error Occurred while setting up your workspace"
      />
    );
  }

  return <>{children}</>;
}
